/**
 * useConversationDecisions.ts — fetch lifecycle for the "Auditoria" decision
 * ledger (decision-ledger design spec §8). Mirrors `useConversationMessages`'s
 * shape (loading/ready/error/unauthorized phases, monotonic request id so a
 * stale response can never commit, a stable `refetch`).
 *
 * 404 (unknown/cross-tenant thread) and 502 (upstream down) both surface as
 * the `error` phase with a retry affordance, same as the message ledger.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { UnauthorizedError } from '../../api/client';
import type { DecisionRow } from '../../api/types';
import floating from '../../lib/floating';

export type ConversationDecisionsPhase = 'loading' | 'ready' | 'error' | 'unauthorized';

export interface ConversationDecisionsState {
  decisions: DecisionRow[];
  phase: ConversationDecisionsPhase;
  refetch: () => void;
}

export const useConversationDecisions = (
  apiClient: ApiClient,
  threadId: string,
): ConversationDecisionsState => {
  const [decisions, setDecisions] = useState<DecisionRow[]>([]);
  const [phase, setPhase] = useState<ConversationDecisionsPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetch = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const data = await apiClient.getConversationDecisions(threadId);
      if (!isCurrent()) return;
      setDecisions(data);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      // 404 / 502 / transport — same clean error+retry state.
      setPhase('error');
    }
  }, [apiClient, threadId]);

  useEffect(() => {
    floating(fetch());
  }, [fetch]);

  return { decisions, phase, refetch: fetch };
};
